import React, { useEffect, useState } from "react";
import { TrashIcon } from "@aureskonnect/react-ui";
import { MdCloudUpload } from "react-icons/md";
import { Card } from "reactstrap";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { setInputImageUploader } from "./store";

import "./index.css";
const REQUIRED_WIDTH = 533;
const REQUIRED_HEIGHT = 300;
const ACCEPTED_TYPES = ["image/png", "image/jpg", "image/jpeg"];

type ImageUploaderPropsType = {
    uploadedImage: string | Blob;
    setUploadedImage: Function;
    id?: string;
    inputImage: boolean;
    setIsEdited: Function;
    setImg: Function;
    isJpgOrPng: boolean;
    setInputImage: Function;
    rowData: any;
    inputImageUploader: boolean;
    errorMessage: string;
    setErrorMessage: Function;
};

function validateImageDimensions(
    file: File
): Promise<{ valid: boolean; width: number; height: number }> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(img.src);
            resolve({
                valid:
                    img.width === REQUIRED_WIDTH &&
                    img.height === REQUIRED_HEIGHT,
                width: img.width,
                height: img.height,
            });
        };
        img.onerror = () => {
            URL.revokeObjectURL(img.src);
            resolve({ valid: false, width: 0, height: 0 });
        };
        img.src = URL.createObjectURL(file);
    });
}

function isValidImageType(file: File): boolean {
    return ACCEPTED_TYPES.includes(file.type);
}

export function ImageUploader({
    uploadedImage,
    setUploadedImage,
    inputImage,
    id = "",
    setIsEdited,
    setImg,
    isJpgOrPng,
    setInputImage,
    inputImageUploader,
    errorMessage,
    setErrorMessage,
}: ImageUploaderPropsType) {
    const [dragging, setDragging] = useState(false);
    const { t } = useTranslation();

    async function onFileChange(e: any) {
        const file = e.target.files[0];
        if (!file) return;

        if (!isValidImageType(file)) {
            setUploadedImage("");
            setErrorMessage(
                t("Unsupported format. Accepted formats: PNG, JPG, JPEG.")
            );
            setInputImageUploader(true);
            e.target.value = "";
            return;
        }

        const dimensions = await validateImageDimensions(file);
        if (!dimensions.valid) {
            setUploadedImage("");
            setErrorMessage(
                t(
                    "Incorrect dimensions ({{width}}×{{height}} px). Required dimensions: {{reqWidth}}×{{reqHeight}} px.",
                    {
                        width: dimensions.width,
                        height: dimensions.height,
                        reqWidth: REQUIRED_WIDTH,
                        reqHeight: REQUIRED_HEIGHT,
                    }
                )
            );
            setInputImageUploader(true);
            e.target.value = "";
            return;
        }

        setErrorMessage("");
        setImg(file.type);
        setUploadedImage(file);
        setIsEdited(true);
        setInputImage(false);
        setInputImageUploader(false);
    }

    const handleClick = () => {
        setUploadedImage("");
        setIsEdited(true);
        setInputImageUploader(false);
        setInputImage(false);
        setErrorMessage("");
    };
    useEffect(() => {
        if (!isJpgOrPng) {
            setInputImageUploader(true);
        } else {
            setInputImageUploader(false);
        }
    }, [isJpgOrPng]);

    return (
        <div
            className="d-flex flex-wrap justify-content-center align-items-center"
            style={{
                border: dragging ? "dashed grey 4px" : "unset",
                borderRadius: "6px",
                height: "100%",
                width: "auto",
                paddingTop: "1rem",
            }}
            onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragging(false);
            }}
            onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();

                setDragging(true);
            }}
            onDrop={async (e) => {
                e.preventDefault();
                e.stopPropagation();

                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    const file = e.dataTransfer.files[0];

                    if (!isValidImageType(file)) {
                        setErrorMessage(
                            t(
                                "Unsupported format. Accepted formats: PNG, JPG, JPEG."
                            )
                        );
                        setInputImageUploader(true);
                        setDragging(false);
                        return;
                    }

                    const dimensions = await validateImageDimensions(file);
                    if (!dimensions.valid) {
                        setUploadedImage("");
                        setErrorMessage(
                            t(
                                "Incorrect dimensions ({{width}}×{{height}} px). Required dimensions: {{reqWidth}}×{{reqHeight}} px.",
                                {
                                    width: dimensions.width,
                                    height: dimensions.height,
                                    reqWidth: REQUIRED_WIDTH,
                                    reqHeight: REQUIRED_HEIGHT,
                                }
                            )
                        );
                        setInputImageUploader(true);
                        setDragging(false);
                        return;
                    }

                    setErrorMessage("");
                    setImg(file.type);
                    setUploadedImage(file);
                    setIsEdited(true);
                    setInputImage(false);
                    setInputImageUploader(false);
                }
                setDragging(false);
            }}
        >
            <Card
                className={classnames("uploader__clz")}
                style={{
                    borderColor:
                        inputImage || inputImageUploader || errorMessage !== ""
                            ? "red"
                            : "",
                    width: "100%",
                }}
            >
                {uploadedImage ? (
                    <React.Fragment>
                        <div
                            className=" d-flex justify-content-center align-items-center"
                            style={{
                                height: "77%",
                                width: "100%",
                            }}
                        >
                            <img
                                src={
                                    typeof uploadedImage === "string"
                                        ? uploadedImage
                                        : URL.createObjectURL(uploadedImage)
                                }
                                width="77%"
                                height="100%"
                                alt=""
                            />
                        </div>
                        <div className=" d-flex justify-content-around">
                            <label
                                htmlFor={`file-${id}`}
                                style={{
                                    cursor: "pointer",
                                }}
                            >
                                <input
                                    type="file"
                                    id={`file-${id}`}
                                    name="imgCollection"
                                    onChange={onFileChange}
                                    accept="image/png,image/jpg,image/jpeg"
                                    style={{
                                        display: "none",
                                    }}
                                />
                            </label>
                            <TrashIcon
                                style={{ cursor: "pointer" }}
                                height={25}
                                width={25}
                                fill="red"
                                onClick={handleClick}
                                className="mt-2"
                            />
                        </div>
                    </React.Fragment>
                ) : (
                    <label htmlFor={`file-${id}`} style={{ cursor: "pointer" }}>
                        <MdCloudUpload
                            style={{
                                width: "139px",
                                height: "165px",
                                minHeight: "95px",
                                maxHeight: "106px",
                                color: "rgba(81, 77, 77, 0.85)",
                            }}
                            className="ml-5"
                        />
                        <div style={{ marginTop: 8 }}>
                            <span
                                style={{
                                    font: "normal normal 900 18px/22px Lato",
                                    color: "#2B2828",
                                }}
                            >
                                {t("Drag and drop your files here or")}
                            </span>
                        </div>

                        <input
                            type="file"
                            id={`file-${id}`}
                            name="imgCollection"
                            onChange={onFileChange}
                            accept="image/png,image/jpg,image/jpeg"
                            style={{ display: "none" }}
                        />
                    </label>
                )}
            </Card>

            {errorMessage && (
                <div
                    style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                        width: "100%",
                        textAlign: "center",
                    }}
                >
                    {errorMessage}
                </div>
            )}
        </div>
    );
}
